import axios from 'axios';

const BASE_URL = 'https://loyalty.bom.asia';
const API_BASE_URL = `${BASE_URL}/api/miniApp/quiz/rewards`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const quizRewardApi = {
  // Get all rewards with filters
  getAllRewards: async (params = {}) => {
    try {
      // Build query string
      const queryParams = new URLSearchParams();
      
      // Filters
      if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);
      if (params.quizId) queryParams.append('quizId', params.quizId);
      
      const queryString = queryParams.toString();
      const url = queryString ? `/all?${queryString}` : '/all';
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get reward by ID
  getRewardById: async (id) => {
    try {
      const response = await api.get(`/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get rewards by quiz ID
  getRewardsByQuizId: async (quizId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);
      
      const queryString = queryParams.toString();
      const url = queryString ? `/quiz/${quizId}?${queryString}` : `/quiz/${quizId}`;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create single reward
  createReward: async (rewardData) => {
    try {
      const response = await api.post('/', rewardData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create bulk rewards
  createBulkRewards: async (quizId, rewards) => {
    try {
      const response = await api.post('/bulk', { quizId, rewards });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update reward
  updateReward: async (id, updateData) => {
    try {
      const response = await api.put(`/${id}`, updateData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete reward
  deleteReward: async (id) => {
    try {
      const response = await api.delete(`/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Toggle reward status (PATCH endpoint)
  toggleRewardActive: async (id) => {
    try {
      const response = await api.patch(`/${id}/toggle`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Helper: Get active rewards
  getActiveRewards: async () => {
    try {
      const response = await api.get('/all?isActive=true');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Helper: Get active rewards by quiz
  getActiveRewardsByQuiz: async (quizId) => {
    return quizRewardApi.getRewardsByQuizId(quizId, { isActive: true });
  },

  // Helper: Create standard reward set
  createStandardRewardSet: async (quizId, totalQuestions) => {
    const rewards = [
      {
        level: 'excellent',
        correctAnswers: Math.ceil(totalQuestions * 0.9),
        message: 'Xuất sắc! Bạn đã hoàn thành xuất sắc bài quiz',
        rewardType: 'points',
        rewardValue: 100,
        isActive: true,
        sortOrder: 1
      },
      {
        level: 'good',
        correctAnswers: Math.ceil(totalQuestions * 0.7),
        message: 'Tốt! Bạn đã làm rất tốt',
        rewardType: 'points',
        rewardValue: 50,
        isActive: true,
        sortOrder: 2
      },
      {
        level: 'average',
        correctAnswers: Math.ceil(totalQuestions * 0.5),
        message: 'Khá! Bạn đã vượt qua',
        rewardType: 'points',
        rewardValue: 25,
        isActive: true,
        sortOrder: 3
      },
      {
        level: 'pass',
        correctAnswers: Math.ceil(totalQuestions * 0.3),
        message: 'Đạt! Bạn cần cố gắng thêm',
        rewardType: 'points',
        rewardValue: 10,
        isActive: true,
        sortOrder: 4
      }
    ];
    
    return quizRewardApi.createBulkRewards(quizId, rewards);
  },

  // Helper: Toggle reward status
  toggleRewardStatus: async (id, currentStatus) => {
    return quizRewardApi.updateReward(id, { isActive: !currentStatus });
  }
};

export default quizRewardApi;

